import { useEffect, useMemo, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot, getDocsFromCache, doc, updateDoc, serverTimestamp, type QuerySnapshot, type DocumentData } from 'firebase/firestore';
import type { Conversation } from '@rsmaids/shared/types/Inbox';
import { dbTwilio, auth } from '../auth/firebase';

export interface SmsConversation extends Conversation {
  _isSms: true;
  smsPhone: string; // E.164 e.g. +14258553424
  hasContactName: boolean;
}

export interface SmsMessage {
  id: string;
  body: string;
  direction: 'inbound' | 'outbound';
  timestamp: number;
  from: string;
  to: string;
  /** Staff member who sent an outbound text (recorded by sendSms; absent on older texts) */
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderStaffId?: string;
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const d = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return phone;
}

// A contactName is only "real" if it has letters in it — a bare phone number
// (however it's formatted) shouldn't be treated as a display name.
function isRealContactName(name: string | undefined): boolean {
  return !!name && /[a-zA-Z]/.test(name);
}

/**
 * Subscribes to the sms-conversations collection and returns items
 * formatted for the inbox conversation list.
 */
export async function markSmsRead(phone: string): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    const ref = doc(dbTwilio, 'sms-conversations', phone);
    await updateDoc(ref, { [`readBy.${uid}`]: serverTimestamp() });
  } catch (e) {
    // A contact who has never texted has no sms-conversations document to mark — nothing to read, not an error
    if ((e as { code?: string })?.code === 'not-found') return;
    console.error('[markSmsRead] failed:', e);
  }
}

export function useSmsInbox(): { conversations: SmsConversation[]; loading: boolean } {
  const [conversations, setConversations] = useState<SmsConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const uid = auth.currentUser?.uid;

  useEffect(() => {
    let mounted = true;
    let currentUnsub: (() => void) | null = null;

    const q = query(
      collection(dbTwilio, 'sms-conversations'),
      orderBy('lastMessageTime', 'desc')
    );

    const subscribe = () => {
      if (!mounted) return;
      currentUnsub = onSnapshot(
        q,
        (snap) => {
          if (!mounted) return;
          const convs: SmsConversation[] = snap.docs.map((d) => {
            // 'estimate': a read marker just written (serverTimestamp, still pending) counts as now, so the row does not
            // flash back to unread until the server confirms it
            const data = d.data({ serverTimestamps: 'estimate' });
            const phone = d.id;
            const digits = phone.replace('+', '');
            const lastMsgTs = data.lastMessageTime?.toMillis ? data.lastMessageTime.toMillis() : Date.now();
            const readByTs = uid && data.readBy?.[uid]?.toMillis ? data.readBy[uid].toMillis() : 0;
            const unread = lastMsgTs > readByTs && (data.unreadCount || 0) > 0;
            return {
              id: `sms-${digits}`,
              name: data.contactName || formatPhone(phone),
              messages: [],
              timestamp: new Date(lastMsgTs),
              avatarUrl: '',
              lastMessage: data.lastMessage || '',
              unread,
              unreadCount: unread ? (data.unreadCount || 0) : 0,
              _isSms: true,
              smsPhone: phone,
              hasContactName: isRealContactName(data.contactName),
            };
          });
          setConversations(convs);
          setLoading(false);
        },
        (err) => {
          console.error('[useSmsInbox] Firestore error, resubscribing in 5s:', err);
          if (mounted) setLoading(false);
          setTimeout(subscribe, 5000);
        }
      );
    };
    subscribe();

    return () => {
      mounted = false;
      currentUnsub?.();
    };
  }, [uid]);

  return { conversations, loading };
}

const EMPTY_BY_PHONE: Record<string, SmsMessage[]> = {};

/** How many of a number's newest texts a thread loads up front (older ones are paged in from Twilio). */
export const SMS_THREAD_PAGE = 30;

/**
 * The newest texts for one or more phone numbers (E.164), merged oldest-first — what a conversation thread
 * shows from the shared sms-conversations store. Only the newest SMS_THREAD_PAGE per number are read (not the
 * whole history), live. `loading` stays true until every number's first snapshot has arrived, so the thread
 * can wait for it and show these together with the chat messages. `mayHaveOlder` is true when any number came
 * back with a full page.
 */
export function useSmsThreads(phones: string[]): { messages: SmsMessage[]; loading: boolean; mayHaveOlder: boolean } {
  const key = phones.filter(Boolean).join('|');
  const [state, setState] = useState<{ key: string; byPhone: Record<string, SmsMessage[]> }>({ key: '', byPhone: {} });

  useEffect(() => {
    if (!key) return;
    let mounted = true;
    const unsubscribers: Array<() => void> = [];

    key.split('|').forEach((phone) => {
      let currentUnsub: (() => void) | null = null;
      const q = query(
        collection(dbTwilio, 'sms-conversations', phone, 'messages'),
        orderBy('timestamp', 'desc'),
        limit(SMS_THREAD_PAGE)
      );
      const put = (msgs: SmsMessage[]) =>
        setState((prev) => ({ key, byPhone: { ...(prev.key === key ? prev.byPhone : {}), [phone]: msgs } }));
      const toMessages = (snap: QuerySnapshot<DocumentData>): SmsMessage[] => snap.docs.map((d) => {
        const data = d.data();
        const ts = data.timestamp?.toMillis ? data.timestamp.toMillis() : (data.timestamp || 0);
        return {
          id: d.id,
          body: data.body || '',
          direction: data.direction as 'inbound' | 'outbound',
          timestamp: ts,
          from: data.from || '',
          to: data.to || '',
          senderStaffName: data.senderStaffName || undefined,
          senderStaffAvatar: data.senderStaffAvatar || undefined,
          senderStaffId: data.senderStaffId || undefined,
        };
      });

      // A live listener on a query it has not run before waits for the server for its first result (about a
      // second). The texts already on this device are read straight from the local cache so the thread can show
      // at once; the listener below then takes over and fills in anything newer.
      const t0 = performance.now();
      void getDocsFromCache(q).then((snap) => {
        console.log(`⏱️ [PERF] Texts ${phone}: local cache read ${(performance.now() - t0).toFixed(0)}ms, ${snap.size} cached`);
        if (!mounted || snap.empty) return;
        setState((prev) => {
          const current = prev.key === key ? prev.byPhone : {};
          return phone in current ? prev : { key, byPhone: { ...current, [phone]: toMessages(snap) } };
        });
      }).catch(() => { /* nothing cached — the listener answers */ });

      const subscribe = () => {
        if (!mounted) return;
        currentUnsub = onSnapshot(
          q,
          (snap) => {
            if (!mounted) return;
            console.log(`⏱️ [PERF] Texts ${phone}: live listener answered after ${(performance.now() - t0).toFixed(0)}ms (${snap.metadata.fromCache ? "from cache" : "from server"}, ${snap.size})`);
            put(toMessages(snap));
          },
          (err) => {
            console.error('[useSmsThreads] Firestore error, resubscribing in 5s:', err.code, err.message, 'phone:', phone);
            // Don't leave the thread waiting on this number
            if (mounted) setState((prev) => (prev.key === key && phone in prev.byPhone ? prev : { key, byPhone: { ...(prev.key === key ? prev.byPhone : {}), [phone]: [] } }));
            setTimeout(subscribe, 5000);
          }
        );
      };
      subscribe();
      unsubscribers.push(() => currentUnsub?.());
    });

    return () => {
      mounted = false;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [key]);

  const list = key ? key.split('|') : [];
  const byPhone = useMemo(() => (state.key === key ? state.byPhone : EMPTY_BY_PHONE), [state, key]);
  const loading = list.length > 0 && !list.every((phone) => phone in byPhone);
  const messages = useMemo(
    () => Object.values(byPhone).flat().sort((a, b) => a.timestamp - b.timestamp),
    [byPhone]
  );
  const mayHaveOlder = Object.values(byPhone).some((msgs) => msgs.length >= SMS_THREAD_PAGE);
  return { messages, loading, mayHaveOlder };
}
